// Blog posts for Empower Legacy Finance
// Each post is plain authored content (no LLM) optimised for AU mortgage SEO.

export const POSTS = [
  {
    slug: "when-to-refinance-your-mortgage",
    title: "When to Refinance Your Mortgage: 7 Signs It's Time",
    description:
      "Australian mortgage rates have shifted again in 2026. Here are seven clear signs your home loan is overdue for a refinance.",
    category: "Refinancing",
    readMinutes: 6,
    publishedAt: "2026-04-22",
    image:
      "https://static.prod-images.emergentagent.com/jobs/1f354b20-e4e3-4a91-b606-8bc67655a4db/images/69fabb000d20ba18d8834728d00e0266ac203d0e39d8e7aab067126d6ade4e2b.png",
    excerpt:
      "If your rate starts with a 6, your fixed term is ending, or you've never had a rate review — it's probably time.",
    blocks: [
      { type: "p", text: "Refinancing isn't just about chasing the lowest rate on a comparison site. Done properly, it restructures your debt around your goals: pay the home off faster, free up cash flow, unlock equity for an investment, or consolidate higher-interest debts." },
      { type: "h2", text: "1. You haven't reviewed your rate in 12+ months" },
      { type: "p", text: "Australian lenders quietly drift their existing customers onto higher 'back book' rates. A 5-minute review often uncovers 0.30–0.80% in immediate savings — that's $90–$240 a month on a $450,000 loan." },
      { type: "h2", text: "2. Your fixed rate is about to roll off" },
      { type: "p", text: "Reverting to a lender's standard variable is almost always a bad deal. Start the refinance conversation 60–90 days before your fixed term ends so settlement lines up with the rollover date." },
      { type: "h2", text: "3. You want to access equity" },
      { type: "p", text: "If your property has grown in value, you may be able to release equity for a renovation, a deposit on an investment property, or to invest into shares — all secured at home loan rates rather than personal loan rates." },
      { type: "h2", text: "4. You're paying LMI you no longer need" },
      { type: "p", text: "Once your loan-to-value ratio drops below 80%, refinancing to a new lender (without LMI) can save you serious money long-term. Note: you can't recover LMI you've already paid, but you can avoid paying more." },
      { type: "h2", text: "5. Cashback offers are stacking up" },
      { type: "p", text: "Even in 2026, a few major lenders still offer $2,000–$4,000 refinance cashbacks. Combined with a sharper rate, the total benefit in year one alone can be $5,000+." },
      { type: "h2", text: "6. Your circumstances have changed" },
      { type: "p", text: "New job, new partner, new baby, or self-employed income? Each of these changes which lenders will give you the sharpest deal. Brokers know which lender suits your exact situation right now." },
      { type: "h2", text: "7. You want to pay your home off faster" },
      { type: "p", text: "The right loan structure (offset, redraw, principal-and-interest, weekly repayments) can knock years off your loan — sometimes without paying a cent more. We'll show you exactly how much earlier you'd be debt-free." },
      { type: "callout", text: "Refinancing through Empower Legacy Finance is free for most home loans — we're paid by the lender at settlement and disclose everything upfront." },
    ],
  },

  {
    slug: "first-home-buyer-checklist",
    title: "First Home Buyer Checklist: From Deposit to Settlement",
    description:
      "A practical, no-nonsense Australian first home buyer checklist covering deposits, pre-approval, inspections, conveyancing and settlement.",
    category: "First Home Buyers",
    readMinutes: 8,
    publishedAt: "2026-05-01",
    image:
      "https://images.unsplash.com/photo-1609253932702-796cbf3d3171?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRpdmVyc2UlMjBmYW1pbHklMjBuZXclMjBob3VzZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzgwNjIyNTZ8MA&ixlib=rb-4.1.0&q=85",
    excerpt:
      "Buying your first home is the biggest financial decision you'll make. Here's the order of operations that actually works.",
    blocks: [
      { type: "h2", text: "Step 1 — Get clear on your numbers (4–8 weeks before searching)" },
      { type: "ul", items: [
        "Pull together 3 months of bank statements, payslips and existing debts.",
        "Estimate your true 'genuine savings' — the deposit lenders care about.",
        "Use a borrowing power calculator to get a rough cap.",
      ]},
      { type: "h2", text: "Step 2 — Talk to a broker (don't go to your bank first)" },
      { type: "p", text: "A broker compares 40+ lenders. Your bank can only offer you their own products. The same payslips can result in a $90,000 difference in borrowing capacity between lenders — this is where you find that out." },
      { type: "h2", text: "Step 3 — Get pre-approved" },
      { type: "p", text: "Pre-approval is a conditional yes from a lender, valid for 60–90 days. It tells real estate agents you're serious and gives you a hard ceiling for offers." },
      { type: "h2", text: "Step 4 — Search with discipline" },
      { type: "ul", items: [
        "Stick to suburbs that align with your budget — not your wishlist.",
        "Always inspect twice (once during the day, once on the weekend).",
        "Order a building & pest inspection before signing anything.",
      ]},
      { type: "h2", text: "Step 5 — Make an offer" },
      { type: "p", text: "Your broker will help you confirm the offer is within your pre-approval. You'll typically pay a 0.25–10% deposit on contract signing (varies by state)." },
      { type: "h2", text: "Step 6 — Formal approval & valuation" },
      { type: "p", text: "Once your offer is accepted, the lender orders a valuation and issues unconditional approval. This usually takes 5–15 business days." },
      { type: "h2", text: "Step 7 — Conveyancing" },
      { type: "p", text: "A conveyancer or solicitor reviews the contract, runs title searches and prepares settlement documents. Budget $1,000–$2,000." },
      { type: "h2", text: "Step 8 — Settlement day 🔑" },
      { type: "p", text: "Funds are transferred, the title is registered in your name, and you collect the keys. Most settlements are now electronic via PEXA." },
      { type: "callout", text: "Want this checklist as a printable PDF? Call us on 0404 123 038 and we'll email it through." },
    ],
  },

  {
    slug: "investment-property-loans-explained",
    title: "Investment Property Loans Explained: Interest-Only vs Principal & Interest",
    description:
      "How investment loans differ from home loans, when to choose interest-only, and the tax considerations every Australian investor should know.",
    category: "Investment",
    readMinutes: 7,
    publishedAt: "2026-05-08",
    image:
      "https://images.unsplash.com/photo-1581345187185-8bd48eb6dca6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhdXN0cmFsaWFuJTIwaG9tZSUyMGV4dGVyaW9yJTIwc3VubGlnaHR8ZW58MHx8fHwxNzc4MDYyMjU2fDA&ixlib=rb-4.1.0&q=85",
    excerpt:
      "Cash flow vs equity build — the structure of your loan determines which one you optimise for.",
    blocks: [
      { type: "p", text: "Investment loans aren't just home loans with a different label. The structure determines your monthly cash flow, tax deductibility, equity growth and how easily you can scale into a second or third property." },
      { type: "h2", text: "How investment loans differ from owner-occupier loans" },
      { type: "ul", items: [
        "Rates are typically 0.20–0.40% higher than owner-occupier rates.",
        "Lenders apply tighter serviceability buffers.",
        "Interest-only periods are far easier to get on investment loans.",
        "All interest charged is generally tax-deductible against rental income.",
      ]},
      { type: "h2", text: "Interest-Only (IO): the cash flow play" },
      { type: "p", text: "On an interest-only loan you don't repay any principal during the IO period (usually 1–5 years). Your monthly cost is lower, freeing up cash flow for other investments or a renovation. Because every dollar of repayment is tax-deductible, IO is popular with investors holding negatively-geared properties." },
      { type: "h2", text: "Principal & Interest (P&I): the equity play" },
      { type: "p", text: "P&I repayments build equity from day one. If you plan to hold long-term and want the property paid off, P&I is mathematically cheaper over the full loan life. Many lenders also offer sharper rates on P&I investment loans." },
      { type: "h2", text: "When to use each" },
      { type: "ul", items: [
        "IO: high marginal tax rate, growing portfolio, redirecting savings to other deposits.",
        "P&I: stable single property, paying down for retirement, lower tax bracket.",
      ]},
      { type: "callout", text: "We model both structures side-by-side for every investor client so you can see the 5-, 10- and 20-year impact before you commit." },
    ],
  },

  {
    slug: "self-employed-home-loans",
    title: "Self-Employed Home Loans: How Brokers Get You Approved",
    description:
      "Sole traders, contractors and SMSF buyers — here's how Australian brokers package self-employed income to get loans approved.",
    category: "Self-Employed",
    readMinutes: 6,
    publishedAt: "2026-05-15",
    image:
      "https://static.prod-images.emergentagent.com/jobs/1f354b20-e4e3-4a91-b606-8bc67655a4db/images/8e3e369577c6366d3075b3f0b289187749a53872b136b7b1d7910d4d2277d1dd.png",
    excerpt:
      "Variable income doesn't have to mean variable answers. With the right lender, self-employed Australians can borrow as much as PAYG buyers.",
    blocks: [
      { type: "p", text: "Banks default to a lazy formula: take your last two years of tax returns, average them, and lend on that figure. For self-employed Australians, this often understates true earning capacity by 20–40%. The right broker knows which lenders look beyond that formula." },
      { type: "h2", text: "Documents you'll need" },
      { type: "ul", items: [
        "Last 2 years of personal and business tax returns (where available).",
        "Last 2 years of company financials (P&L and balance sheet).",
        "Most recent BAS statements.",
        "Business bank statements (3–6 months).",
        "ABN active for at least 12–24 months (some lenders accept 6 months).",
      ]},
      { type: "h2", text: "Three lender 'lanes' for self-employed buyers" },
      { type: "ul", items: [
        "Full-doc — two years of returns; sharpest rates, strictest assessment.",
        "One-year doc — only one tax return needed; small premium on rate.",
        "Alt-doc / low-doc — BAS or accountant's letter only; rate premium 0.30–1.00%, but available where full-doc isn't.",
      ]},
      { type: "h2", text: "How we strengthen your application" },
      { type: "ul", items: [
        "Add back items the ATO penalises but lenders accept (depreciation, one-off expenses, owner wages reinvested).",
        "Choose a lender that accepts your industry's typical income volatility.",
        "Time the application to land just after your strongest BAS quarter.",
      ]},
      { type: "h2", text: "SMSF property purchases" },
      { type: "p", text: "Self-Managed Super Fund loans are a specialist niche — only a handful of lenders write them, and the structure (LRBA / bare trust) must be perfect. We work with the SMSF specialists daily." },
      { type: "callout", text: "Got knocked back somewhere else? Most self-employed declines we see are fixable. Call us for a second opinion." },
    ],
  },
];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);
